import { apiFetch } from "./api-fetch";
import type { AgentCredentialPublicKey, EncryptedCredential } from "./types";

const CREDENTIAL_VAULT_BASE = "/api/credential-vault";

export type CredentialVaultCredentialKind = string;

export type CredentialVaultEncryptedSecret = EncryptedCredential;
export type CredentialVaultAgentPublicKey = AgentCredentialPublicKey;

export interface CredentialVaultCredential {
  id: string;
  ref?: string | null;
  kind: CredentialVaultCredentialKind;
  label?: string | null;
  username?: string | null;
  domain?: string | null;
  hostId?: string | null;
  targetHost?: string | null;
  clientId?: string | null;
  siteId?: string | null;
  tenantId?: string | null;
  metadata?: Record<string, unknown> | null;
  createdByUserId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
}

export interface ListCredentialVaultCredentialsParams {
  hostId?: string;
  clientId?: string;
  siteId?: string;
  kind?: CredentialVaultCredentialKind;
  search?: string;
}

export interface ListCredentialVaultCredentialsResponse {
  items: CredentialVaultCredential[];
}

export interface CreateCredentialVaultCredentialRequest {
  ref?: string;
  kind?: CredentialVaultCredentialKind;
  label?: string;
  username?: string;
  domain?: string | null;
  hostId?: string | null;
  targetHost?: string | null;
  clientId?: string | null;
  siteId?: string | null;
  password?: string;
  secret?: string;
  encryptedSecret?: CredentialVaultEncryptedSecret;
  metadata?: Record<string, unknown>;
}

export interface CreateCredentialVaultCredentialResponse {
  credential: CredentialVaultCredential;
}

export interface SealCredentialForAgentRequest {
  sessionCode?: string;
  hostId?: string;
  agentPublicKey: CredentialVaultAgentPublicKey;
  requestId?: string;
  purpose?: string;
  ttlSeconds?: number;
}

export interface SealCredentialForAgentResponse {
  credentialId: string;
  ref?: string | null;
  grantId?: string;
  username?: string;
  domain?: string;
  sealed?: CredentialVaultEncryptedSecret;
  credential?: CredentialVaultEncryptedSecret;
  expiresAt?: string | null;
}

export interface DirectRdpGrantRequest {
  sessionCode?: string;
  hostId?: string;
  targetHost?: string;
  targetPort?: number;
  operatorPublicKey?: CredentialVaultAgentPublicKey;
  publicKey?: CredentialVaultAgentPublicKey;
  tunnelId?: string;
  operatorPeerId?: string;
  action?: "view" | "control";
  ttlSeconds?: number;
}

export interface DirectRdpGrantResponse {
  grantId: string;
  credentialId: string;
  sessionCode?: string | null;
  hostId?: string | null;
  targetHost?: string | null;
  targetPort?: number | null;
  action?: string;
  expiresAt?: string | null;
  username?: string;
  domain?: string;
  credential?: CredentialVaultEncryptedSecret;
}

interface ApiErrorBody {
  error?: string;
  message?: string;
}

function credentialsPath(params: ListCredentialVaultCredentialsParams = {}): string {
  const qs = new URLSearchParams();
  if (params.hostId) qs.set("hostId", params.hostId);
  if (params.clientId) qs.set("clientId", params.clientId);
  if (params.siteId) qs.set("siteId", params.siteId);
  if (params.kind) qs.set("kind", params.kind);
  if (params.search) qs.set("search", params.search);
  const suffix = qs.toString();
  return `${CREDENTIAL_VAULT_BASE}/credentials${suffix ? `?${suffix}` : ""}`;
}

async function readJson<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");

  const response = await apiFetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.message || body.error || `HTTP ${response.status}`);
  }
  return readJson<T>(response);
}

export async function listCredentialVaultCredentials(
  params: ListCredentialVaultCredentialsParams = {},
): Promise<ListCredentialVaultCredentialsResponse> {
  return requestJson<ListCredentialVaultCredentialsResponse>(credentialsPath(params), { method: "GET" });
}

export async function createCredentialVaultCredential(
  input: CreateCredentialVaultCredentialRequest,
): Promise<CreateCredentialVaultCredentialResponse> {
  return requestJson<CreateCredentialVaultCredentialResponse>(`${CREDENTIAL_VAULT_BASE}/credentials`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function sealCredentialVaultCredentialForAgent(
  credentialId: string,
  input: SealCredentialForAgentRequest,
): Promise<SealCredentialForAgentResponse> {
  return requestJson<SealCredentialForAgentResponse>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}/seal-for-agent`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function requestCredentialVaultDirectRdpGrant(
  credentialId: string,
  input: DirectRdpGrantRequest,
): Promise<DirectRdpGrantResponse> {
  return requestJson<DirectRdpGrantResponse>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}/direct-rdp-grant`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

// ── Extension VOLT : édition / révocation / ACL ──────────────────────────

export interface UpdateCredentialVaultCredentialRequest {
  label?: string;
  username?: string;
  domain?: string | null;
  kind?: CredentialVaultCredentialKind;
  clientId?: string | null;
  siteId?: string | null;
  metadata?: Record<string, unknown>;
  password?: string;
}

export interface UpdateCredentialVaultCredentialResponse {
  credential: CredentialVaultCredential;
}

export async function updateCredentialVaultCredential(
  credentialId: string,
  input: UpdateCredentialVaultCredentialRequest,
): Promise<UpdateCredentialVaultCredentialResponse> {
  return requestJson<UpdateCredentialVaultCredentialResponse>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export interface RevokeCredentialVaultCredentialResponse {
  revoked: boolean;
  credentialId: string;
}

export async function revokeCredentialVaultCredential(
  credentialId: string,
): Promise<RevokeCredentialVaultCredentialResponse> {
  return requestJson<RevokeCredentialVaultCredentialResponse>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}`,
    { method: "DELETE" },
  );
}

export type CredentialVaultPrincipalType = "user" | "tenant" | "group";

export interface CredentialVaultAclEntry {
  principalType: CredentialVaultPrincipalType;
  principalId: string;
  actions: string[];
  createdByUserId?: string | null;
  createdAt?: string | null;
  revokedAt?: string | null;
}

export interface ListCredentialVaultAclResponse {
  items: CredentialVaultAclEntry[];
}

export interface SetCredentialVaultAclRequest {
  principalType: CredentialVaultPrincipalType;
  principalId: string;
  actions: string[];
}

export interface SetCredentialVaultAclResponse {
  set: boolean;
  credentialId: string;
  principalType: CredentialVaultPrincipalType;
  principalId: string;
  actions: string[];
}

export async function listCredentialVaultAcl(credentialId: string): Promise<ListCredentialVaultAclResponse> {
  return requestJson<ListCredentialVaultAclResponse>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}/acl`,
    { method: "GET" },
  );
}

export async function setCredentialVaultAcl(
  credentialId: string,
  input: SetCredentialVaultAclRequest,
): Promise<SetCredentialVaultAclResponse> {
  return requestJson<SetCredentialVaultAclResponse>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}/acl`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function revokeCredentialVaultAcl(
  credentialId: string,
  principalType: CredentialVaultPrincipalType,
  principalId: string,
): Promise<{ revoked: boolean }> {
  const qs = new URLSearchParams({ principalType, principalId });
  return requestJson<{ revoked: boolean }>(
    `${CREDENTIAL_VAULT_BASE}/credentials/${encodeURIComponent(credentialId)}/acl?${qs.toString()}`,
    { method: "DELETE" },
  );
}
